<template>
	<div class="container">
		<div class="page">
			<!-- Header -->
			<div class="page-header">
				<div class="row">
					<div class="col-md-9">
						<h1 class="page-title">{{ $t('Brand Monitoring') }}</h1>
					</div>
				</div>
			</div>

			<!-- Table Menu -->
			<div class="table-list-menu d-lg-flex align-items-center">

				<div class="table-actions d-flex align-items-center flex-wrap">
					<!-- Search Keyword -->
					<input type="text" class="input form-control form-control-sm products-search-box"
						:placeholder="`${$t('Search')}..`" @input="onKeywordChange" v-model="keyword">
				</div>

				<div class="table-filters text-right d-flex align-items-center ml-a">
					<!-- Clear Filter -->
					<div class="d-flex align-items-center">
						<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
							{{ $t('Clear Filter') }}
						</button>
					</div>
					<!-- Date Filter -->
					<daterangepicker :presets="filterDatePresets" :start="since" :end="until" @selected="setFilterDate">
					</daterangepicker>
				</div>
			</div>

			<div class="products-list table-responsive">
				<table class="products-list-table table table-primary table-striped mb-0">
					<thead>
						<tr>
							<td width="5%">No.</td>
							<td>Data Seller</td>
							<td>Kontak</td>
							<td>Alamat</td>
							<td>Source</td>
							<td>Tanggal</td>
							<td>Status</td>
							<td>Action</td>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(brand, idx) in brands" :key="brand._id.$oid">
							<td>{{ idx + 1 }}</td>
							<td class="mw-150">
								{{ brand.name }}
								<br />
								{{ brand.store_name }}
							</td>
							<td class="mw-150">
								{{ brand.email }}
								<br />
								{{ brand.phone }}
							</td>
							<td class="mw-250">
								{{ brand.customer_address.address }}
								<br />
								{{ formatCityLabel(brand.customer_address) }}
							</td>
							<td class="mw-150">{{ formatSource(brand) }}</td>
							<td>{{ formatDate(brand.created_at) }}</td>
							<td>
								<span :class="`text-${formatStatus(brand.status).color}`">{{
									formatStatus(brand.status).label
								}}</span>
							</td>
							<td class="d-flex flex-nowrap text-primary">
								<i class="bi bi-whatsapp action-icon" @click="followUp(brand)"></i>
								<i class="bi bi-card-text action-icon" @click="editBrand(brand)"></i>
								<div v-if="isStatusUpdating" class="is-loading mx-4"></div>
								<div v-else>
									<i class="bi bi-pencil-square action-icon" data-toggle="dropdown"
										aria-expanded="false"></i>
									<div class="dropdown-menu">
										<button v-for="status in getStatusOptions(brand.status)" :key="status.value"
											type="button" class="dropdown-item"
											@click="updateStatus(status.value, brand._id.$oid, idx)"><span
												:class="`text-${status.color}`">{{ status.label }}</span></button>
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<div class="list-empty table-empty" v-if="brands.length === 0 && isFetching === false">
					<i class="material-icons mr-2 va-m">error_outline</i>
					<span class="va-m">{{ $t('No brands was found') }}</span>
				</div>
			</div>

			<!-- Table Loader -->
			<div class="table-fetch-status" v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Modals -->
			<div class="modals">

				<!-- Modal Detail -->
				<modal class="table-filter-modal" size="medium" @close="isShowEdit = false" v-if="isShowEdit">
					<div slot="header">
						<h3>Brand Detail</h3>
					</div>
					<div slot="body">
						<div class="row">

							<div class="col-md-6">
								<div class="form-group">
									<label>Nama <span class="text-danger">*</span></label>
									<input type="text" class="form-control" :class="{ 'is-invalid': errorMessages.name }"
										placeholder="Masukkan Nama Anda..." v-model="name" required />
									<small v-if="errorMessages.name" class="invalid-feedback">{{ errorMessages.name
									}}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-group">
									<label>Nama Perusahaan / Toko</label>
									<input type="text" class="form-control"
										:class="{ 'is-invalid': errorMessages.storeName }"
										placeholder="Masukkan Nama Perusahaan..." v-model="storeName" required />
									<small v-if="errorMessages.storeName" class="invalid-feedback">{{
										errorMessages.storeName
									}}</small>
								</div>
							</div>
							<div class="col-md-6">
								<div class="form-group">
									<label>Email Bisnis <span class="text-danger">*</span></label>
									<input type="email" class="form-control" :class="{ 'is-invalid': errorMessages.email }"
										placeholder="Masukkan Email Bisnis Anda..." v-model="email" required />
									<small v-if="errorMessages.email" class="invalid-feedback">{{ errorMessages.email
									}}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-group">
									<label>Nomor Telepon / WA <span class="text-danger">*</span></label>
									<div class="input-group">
										<div class="input-group-prepend">
											<span class="input-group-text">{{ countryCode }}</span>
										</div>
										<input type="text" name="phone" class="form-control"
											:class="{ 'is-invalid': errorMessages.phone }"
											placeholder="Masukkan Nomor Telepon Anda..." v-model.trim="phone"
											@input="phoneValidation" minlength="9" required>
										<small v-if="errorMessages.phone" class="invalid-feedback">{{ errorMessages.phone
										}}</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Kota <span class="text-danger">*</span></label>
									<multiselect v-model="selectedSubdistrict" :custom-label="cityLabel"
										:show-labels="false" track-by="subdistrict_id" @close="subdistrictValidation"
										placeholder="Masukkan Kota / Kecamatan (minimal 3 huruf)" :options="subdistricts"
										@search-change="fetchSubdistricts">
									</multiselect>
									<small v-if="errorMessages.subdistrict" class="text-danger">{{ errorMessages.subdistrict
									}}</small>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Alamat Lengkap <span class="text-danger">*</span></label>
									<textarea class="form-control" :class="{ 'is-invalid': errorMessages.address }"
										placeholder="Masukkan Alamat Lengkap..." rows="3" required
										v-model="address"></textarea>
									<small v-if="errorMessages.address" class="invalid-feedback">{{ errorMessages.address
									}}</small>
								</div>
							</div>

							<!-- Categories -->
							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Jenis Produk Yang Diminati <span class="text-danger">*</span></label>
										<multiselect placeholder="Select Category" label="name" track-by="slug"
											@select="onCategoryChange" @close="categoryValidation"
											v-model="selectedCategory" :options="categories" :show-labels="false"
											:allow-empty="false" :disabled="categories.length === 0">
										</multiselect>
										<small v-if="errorMessages.category" class="text-danger">{{ errorMessages.category
										}}</small>
									</div>

									<div>
										<multiselect v-if="subCategories" placeholder="Select Sub Category" label="name" track-by="slug"
											v-model="selectedSubCategory"
											:options="subCategories" :show-labels="false" :allow-empty="false"
											:disabled="subCategories.length === 0">
										</multiselect>
										<small v-if="errorMessages.subCategory" class="text-danger">{{
											errorMessages.subCategory }}</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Deskripsi Produk (Spesifikasi Produk) <span class="text-danger">*</span></label>
									<textarea class="form-control" :class="{ 'is-invalid': errorMessages.description }"
										placeholder="Masukkan Deskripsi Produk..." rows="3" required
										v-model="description"></textarea>
									<small v-if="errorMessages.description" class="invalid-feedback">{{
										errorMessages.description }}</small>
								</div>
							</div>

							<div class="row mx-0 w-100">
								<div class="col-md-12">
									<label>Contoh Foto Produk</label>
									<p class="pc-p">
										Format gambar .jpg .jpeg .png dan ukuran minimum 300 x 300px (Untuk gambar
										optimal gunakan ukuran minimum 700 x 700 px). Ukuran maksimum gambar adalah
										<b>2MB</b>.
									</p>
									<small v-if="errorMessages.image" class="text-danger">{{ errorMessages.image }}</small>
								</div>
								<div class="col-md-12">
									<div class="box__content">
										<div class="pc-images-list">
											<draggable draggable=".draggable" v-model="product.images" v-bind="dragOptions"
												@start="drag = true" @end="drag = false">
												<transition-group class="row small-gutter" type="transition"
													:name="!drag ? 'flip-list' : null">
													<div class="col" :class="{ draggable: image !== '' }"
														v-for="(image, index) in product.images" :key="index + 0">
														<div v-if="isLoading && image === ''" class="pc-image-empty">
															<div class="image-loading">
																<div class="is-loading"></div>
															</div>
														</div>
														<div v-else-if="image === ''" class="pc-image-empty cursor-pointer"
															@click="uploadImages(index)">
															<div class="image-upload-placeholder">
																<i class="fi fi-rr-picture image-placeholder"></i>
																<div class="text-placeholder">
																	Foto {{ index + 1 }}
																</div>
															</div>
														</div>
														<div v-else class="pc-image" :class="{
															'is-loading': image === '',
														}">
															<div class="img"
																:style="{ backgroundImage: `url('${image}')` }">
															</div>
															<i class="material-icons" v-tooltip="'Remove'"
																@click="removeProductImages(index)">close</i>
														</div>
													</div>
												</transition-group>
											</draggable>
										</div>
										<div class="form-group buttons m-0 hidden">
											<input type="file" accept="image/*" class="input-file" ref="imageUploader"
												id="pc-product-images-file" @change="onProductImagesChange" />
										</div>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Link Referensi Produk</label>
									<input type="text" class="form-control" placeholder="https://"
										v-model.trim="referenceLink">
									<small v-if="errorMessages.referenceLink" class="text-danger">{{
										errorMessages.referenceLink
									}}</small>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Dari mana Anda mengetahui tentang Ostock <span class="text-danger">*</span></label>
									<select v-model="selectedSource" @change="sourceValidation" class="form-control">
										<option value="" disabled selected>Pilih Sumber</option>
										<option :value="source.value" v-for="source in sourceOptions" :key="source.value">{{
											source.label }}</option>
									</select>
									<small v-if="errorMessages.selectedSource" class="text-danger">{{
										errorMessages.selectedSource }}</small>
								</div>
							</div>

							<div class="col-md-12" v-if="isOtherSource">
								<div class="form-group">
									<label>Masukkan Sumber Lainnya <span class="text-danger">*</span></label>
									<input type="text" class="form-control" placeholder="Masukkan Sumber Lainnya"
										@keyup="sourceValueValidation" v-model.trim="sourceValue">
									<small v-if="errorMessages.sourceValue" class="text-danger">{{ errorMessages.sourceValue
									}}</small>
								</div>
							</div>

						</div>
					</div>
					<div slot="footer">
						<button type="button" class="btn btn-light" @click="isShowEdit = false">
							{{ $t('Close') }}
						</button>
						<button type="button" class="btn btn-primary" :class="{ 'is-loading': isUpdateBrand }"
							@click="updateBrand">
							Update
						</button>
					</div>
				</modal>

				<!-- Follow Up -->
				<follow-up-modal :name="followUpName" :number="followUpNumber" :message="followUpMessage"
					@close="hideFollowUp" v-if="isFollowUpVisible" />

			</div>

		</div>
		<notifications group="brands" position="bottom right" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import adminApi from '@/api/admin';
import brandApi from '@/api/brand';
import homepageApi from '@/api/homepage';
import mediaApi from '@/api/media';
import shippingApi from '@/api/shipping';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Modal from '@/components/Modal.vue';
import FollowUpModal from '@/components/FollowUpModal.vue';
import {
	getDateRange,
	duplicateVar,
	formatWaNumber,
	getAxiosErrorMessage,
	isUrlValid,
} from '@/lib/helper';
import { statusOptions, sourceOptions } from '@/lib/statics';

const defaultFilterDate = getDateRange();

export default {
	name: 'brands',
	components: {
		Daterangepicker,
		FollowUpModal,
		Modal,
		draggable,
	},
	data() {
		return {
			page: 1,
			keyword: '',
			limit: 50,
			sort_by: 'created_at',
			sort: 'desc',
			filterDate: defaultFilterDate,
			isAll: false,
			isFetching: false,
			isStatusUpdating: false,
			isShowEdit: false,
			isFetchingCategories: false,
			categories: [],
			subCategories: [],
			selectedCategory: null,
			selectedSubCategory: null,
			description: '',
			isLoading: false, // is uploading images
			drag: false,
			dragOptions: {
				animation: 100,
			},
			product: {
				images: ['', '', '', '', ''],
			},
			productImages: [],
			productImagesLimit: 5,
			referenceLink: '',
			sourceOptions,
			selectedSource: '',
			sourceValue: '',
			brands: [],
			isUpdateBrand: false,
			name: '',
			storeName: '',
			email: '',
			countryCode: '+62',
			phone: '',
			isLoadingSubdistrics: false,
			subdistrictKeyword: '',
			subdistricts: [],
			selectedSubdistrict: null,
			address: '',
			statusOptions,
			errorMessages: {
				// name: '',
				// storeName: '',
				// email: '',
				// phone: '',
				subdistrict: '',
				address: '',
				category: '',
				subCategory: '',
				description: '',
				image: '',
				referenceLink: '',
				selectedSource: '',
				sourceValue: '',
			},
			followUpNumber: '',
			followUpName: '',
			followUpMessage: '',
			followUpMessages: '',
			isFollowUpVisible: false,
		};
	},
	methods: {
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchBrands(false);
				}
			}
		},
		fetchBrands(reset = true) {
			if (reset) {
				this.resetBrands();
			}

			this.isFetching = true;
			const params = this.params;

			const callback = (response) => {
				const brands = response.data;
				this.brands = this.brands.concat(brands);
				this.isAll = brands.length < this.limit;
				this.page++;
				this.isFetching = false;
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				// eslint-disable-next-line
				console.log(msg);
			};
			adminApi.getBrands(params, callback, errorCallback);
		},
		resetBrands() {
			this.brands = [];
			this.isAll = false;
			this.page = 1;
		},
		formatCityLabel(customerAddress) {
			return `${customerAddress.province_name}, ${customerAddress.city_name_with_type}, ${customerAddress.subdistrict_name}`;
		},
		formatSource(brand) {
			const empty = '-';
			const sourceKey = brand.source ? brand.source.key : '';
			if (!sourceKey) {
				return empty;
			}
			const selectedSource = this.sourceOptions.find(source => source.value === sourceKey);
			if (!selectedSource) {
				return empty;
			}
			let sourceLabel = selectedSource.label;
			if (sourceKey === 'others') {
				sourceLabel = `Lainnya : ${brand.source.value}`;
			}
			return sourceLabel;
		},
		formatDate(dateString) {
			const dateNumberLong = date => new Date(parseInt(date.$date.$numberLong));
			const date = typeof dateString === 'object' ? dateNumberLong(dateString) : dateString;
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			const formattedDate = this.moment
				.utc(date)
				.tz(timezone)
				.format('DD MMM YYYY');
			return dateString && dateString !== '' ? formattedDate : '-';
		},
		formatStatus(statusValue) {
			const selectedStatus = this.statusOptions.find(status => status.value === statusValue);
			return {
				label: selectedStatus?.label,
				color: selectedStatus?.color,
			};
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchBrands();
			}, 1000);
		},
		clearFilter() {
			window.location.href = '/admin/brand';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchBrands();
		},
		getStatusOptions(statusValue) {
			return this.statusOptions.filter(status => status.value !== statusValue);
		},
		updateStatus(status, brandId, idx) {
			this.isStatusUpdating = true;
			const params = {
				status,
			};

			const callback = (response) => {
				const message = response.message;
				const brand = this.brands[idx];
				brand.status = status;
				this.brands[idx] = brand;
				this.$notify({
					group: 'brands',
					type: 'success',
					title: 'Success',
					text: message,
				});
				this.isStatusUpdating = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				this.$notify({
					group: 'brands',
					type: 'error',
					title: 'Failed',
					text: msg,
				});
				console.log(msg);
				this.isStatusUpdating = false;
			};

			adminApi.updateBrand(brandId, params, callback, errorCallback);
		},
		getSubdistricts() {
			this.isLoadingSubdistrics = true;
			const subdistrictIds = JSON.stringify(this.originIds);
			const params = {
				page: 1,
				subdistrict_ids: subdistrictIds,
				get_complete_data: true,
				keyword: this.subdistrictKeyword,
			};
			const callback = (response) => {
				const subdistricts = response.data;
				this.subdistricts = subdistricts;
				this.isLoadingSubdistrics = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Get Subdistricts',
					text: message,
				});
				this.isLoadingSubdistrics = false;
			};
			shippingApi.getCompleteSubdistricts(params, callback, errorCallback);
		},
		fetchSubdistricts(query) {
			if (query.length >= 3) {
				this.subdistrictKeyword = query;
				this.getSubdistricts();
			}
		},
		// eslint-disable-next-line camelcase
		cityLabel({ province_name, city_name_with_type, subdistrict_name }) {
			// eslint-disable-next-line camelcase
			return `${province_name}, ${city_name_with_type}, ${subdistrict_name}`;
		},
		subdistrictValidation() {
			if (this.selectedSubdistrict) {
				this.errorMessages.subdistrict = '';
			} else {
				this.errorMessages.subdistrict = 'Kota Wajib Dipilih';
			}
		},
		nameValidation() {
			if (this.name.length <= 0) {
				this.errorMessages.name = 'Nama Wajib Diisi';
			} else {
				this.errorMessages.name = '';
			}
		},
		storeNameValidation() {
			if (this.storeName.length <= 0) {
				this.errorMessages.storeName = 'Nama Perusahaan Wajib Diisi';
			} else {
				this.errorMessages.storeName = '';
			}
		},
		isEmailValid(email) {
			if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
				return true;
			}
			return false;
		},
		emailValidation() {
			if (this.email.length <= 0) {
				this.errorMessages.email = 'Email Wajib Diisi';
			} else if (!this.isEmailValid(this.email)) {
				this.errorMessages.email = 'Email Tidak Valid';
			} else {
				this.errorMessages.email = '';
			}
		},
		isPhoneError() {
			return (
				this.phone.trim() !== ''
				&& !new RegExp(/^\+\d+$/).test(this.phone.trim())
			);
		},
		phoneValidation() {
			const validPhone = this.isPhoneError();
			this.phone = this.phone.replace(/[^\d]/g, '');
			if (validPhone) {
				const firstSelection = this.phone.substring(0, 1);
				const secondSelection = this.phone.substring(0, 2);
				let phone = this.phone;
				if (secondSelection === '62') {
					phone = phone.replace('62', '');
				} else if (firstSelection === '0') {
					phone = phone.replace('0', '');
				}
				this.phone = phone;
				if (this.phone.length < 9) {
					this.errorMessages.phone = 'Nomor Telepon Minimal 9 Digit';
				} else {
					this.errorMessages.phone = '';
				}
			} else {
				this.errorMessages.phone = 'Nomor Telepon Tidak Valid';
			}
		},
		addressValidation() {
			if (this.address.length <= 0) {
				this.errorMessages.address = 'Alamat Lengkap Wajib Diisi';
			} else {
				this.errorMessages.address = '';
			}
		},
		categoryValidation() {
			if (this.selectedCategory
				&& this.selectedCategory.slug !== 'uncategorized'
				&& this.selectedCategory.slug !== 'produk-lainnya') {
				this.errorMessages.category = '';
			} else {
				this.errorMessages.category = 'Kategori Wajib Dipilih';
			}
		},
		subCategoryValidation() {
			if (this.selectedSubCategory) {
				this.errorMessages.subCategory = '';
			} else {
				this.errorMessages.subCategory = 'Sub Kategori Wajib Dipilih';
			}
		},
		descriptionValidation() {
			if (this.description.length <= 0) {
				this.errorMessages.description = 'Deskripsi Wajib Diisi';
			} else {
				this.errorMessages.description = '';
			}
		},
		fetchCategories() {
			this.isFetchingCategories = true;
			const callback = (response) => {
				this.isFetchingCategories = false;
				const categories = response.data;
				this.categories = categories;
			};
			const errorCallback = () => {
				this.isFetchingCategories = false;
			};
			homepageApi.getAllCategories(callback, errorCallback);
		},
		onCategoryChange(category) {
			this.selectedSubCategory = null;
			this.subCategories = category?.sub_categories;
		},
		uploadImages(index) {
			this.currentIndexUploadImage = index;
			const inputFile = this.$refs.imageUploader;
			inputFile.click();
		},
		onProductImagesChange(event) {
			this.isLoading = true;
			const maxUploadSize = 2; // MB
			const input = event.target;
			for (let i = 0; i < input.files.length; i++) {
				const image = input.files[i];

				// Check File Size
				let fileSize = image.size;
				fileSize /= 1048576;

				// Limit Images
				const isLimitReached =
					this.productImages.length === this.productImagesLimit;
				const isFileSizeTooLarge = fileSize > maxUploadSize;

				// Show Notice if size too large
				if (isFileSizeTooLarge) {
					const noticeMessage = `'${image.name}' size is too large (max 2MB)`;
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				if (isLimitReached) {
					const noticeMessage = 'Maksimum upload 5 foto produk';
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				this.productImages.push(image);

				const previewIndex = this.currentIndexUploadImage;

				this.$set(this.product.images, previewIndex, '');

				// Load the image file into a canvas element
				const img = new Image();
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				const reader = new FileReader();
				// eslint-disable-next-line no-loop-func
				reader.onload = () => {
					img.onload = () => {
						const size = Math.max(img.width, img.height);
						canvas.width = size;
						canvas.height = size;
						const offsetX = (size - img.width) / 2;
						const offsetY = (size - img.height) / 2;
						ctx.fillStyle = '#fff';
						ctx.fillRect(0, 0, size, size); // fill the canvas with white
						ctx.drawImage(img, offsetX, offsetY);

						// Convert the canvas to a blob and upload it to the server
						canvas.toBlob((blob) => {
							// Start Upload
							const uploadParams = new FormData();
							uploadParams.append('image', blob);

							const callback = (response) => {
								this.isLoading = false;
								const path = response.data;
								const url = `${this.mediaUrlPrefix}/${path}`;
								this.$set(this.product.images, previewIndex, url);
								this.linkImageValidation();
							};

							const errorCallback = () => {
								this.isLoading = false;
								// eslint-disable-next-line
								this.productImages.splice(previewIndex, 1);
								this.product.images.splice(previewIndex, 1);
								this.linkImageValidation();
							};

							// Upload Image
							mediaApi.uploadImagePublic(uploadParams, callback, errorCallback);
						}, image.type, 0.9);
					};
					img.src = reader.result;
				};
				reader.readAsDataURL(image);
			}
			event.target.files = null;
			event.target.value = '';
		},
		removeProductImages(index) {
			this.productImages.splice(index, 1);
			this.product.images.splice(index, 1, '');
		},
		linkImageValidation() {
			const image = this.product.images.find(img => img !== '');
			if (!image && !this.referenceLink) {
				this.errorMessages.image = 'Upload minimal 1 gambar atau isi Link Referensi';
				this.errorMessages.referenceLink = 'Isi Link Referensi atau Upload minimal 1 gambar';
			} else if (this.referenceLink && !isUrlValid(this.referenceLink)) {
				this.errorMessages.referenceLink = 'Link harus valid dan diawali https:// atau http://';
			} else {
				this.errorMessages.image = '';
				this.errorMessages.referenceLink = '';
			}
		},
		sourceValidation() {
			if (this.selectedSource === '') {
				this.errorMessages.selectedSource = 'Source Wajib Dipilih';
			} else {
				this.errorMessages.selectedSource = '';
			}
		},
		sourceValueValidation() {
			if (this.selectedSource === 'others' && this.sourceValue === '') {
				this.errorMessages.sourceValue = 'Source Wajib Diisi';
			} else {
				this.errorMessages.sourceValue = '';
			}
		},
		validateAllForm() {
			this.nameValidation();
			// this.storeNameValidation();
			this.emailValidation();
			this.phoneValidation();
			this.subdistrictValidation();
			this.addressValidation();
			this.categoryValidation();
			// this.subCategoryValidation();
			this.descriptionValidation();
			this.linkImageValidation();
			this.sourceValidation();
			this.sourceValueValidation();
		},
		assignCategory(selectedBrand) {
			this.selectedCategory = selectedBrand.category;
			const categories = this.categories.find(category => category.slug === this.selectedCategory.slug);
			this.onCategoryChange(categories);
			const category = selectedBrand.category;
			const subCategory = category.sub_category ? category.sub_category : null;
			this.selectedSubCategory = subCategory;
		},
		assignSource(selectedBrand) {
			const source = selectedBrand.source || null;
			this.selectedSource = source ? source.key : '';
			this.sourceValue = this.isOtherSource ? source.value : '';
		},
		editBrand(brand) {
			this.selectedBrand = brand;
			this.name = this.selectedBrand.name;
			this.storeName = this.selectedBrand.store_name;
			this.email = this.selectedBrand.email;
			this.phone = this.selectedBrand.phone.startsWith('+62') ? this.selectedBrand.phone.replace('+62', '') : this.selectedBrand.phone;
			this.selectedSubdistrict = this.selectedBrand.customer_address;
			this.address = this.selectedBrand.customer_address.address;
			this.assignCategory(this.selectedBrand);
			this.description = this.selectedBrand.description || '';
			this.product.images = this.selectedBrand.product_images;
			this.referenceLink = this.selectedBrand.reference_link;
			this.assignSource(this.selectedBrand);
			this.isShowEdit = true;
		},
		getCategoryParams() {
			const selectedSubCategory = duplicateVar(this.selectedSubCategory);
			const selectedCategory = duplicateVar(this.selectedCategory);
			delete selectedSubCategory.sub_sub_categories;
			delete selectedCategory.sub_categories;
			delete selectedCategory._id;
			delete selectedSubCategory._id;
			delete selectedSubCategory.parent_id;

			const category = {
				...selectedCategory,
				sub_category: selectedSubCategory,
			};

			return category;
		},
		getSourceParams() {
			const source = {
				key: this.selectedSource,
				value: this.isOtherSource ? this.sourceValue : this.selectedSource,
			};

			return source;
		},
		updateBrand() {
			this.validateAllForm();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isUpdateBrand = true;

			this.selectedSubdistrict.address = this.address;
			const category = this.getCategoryParams();
			const source = this.getSourceParams();

			const params = {
				...this.selectedBrand,
				name: this.name,
				store_name: this.storeName,
				phone: `${this.countryCode}${this.phone}`,
				email: this.email,
				customer_address: this.selectedSubdistrict,
				category,
				description: this.description,
				product_images: this.product.images,
				reference_link: this.referenceLink,
				source,
			};

			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'app',
					title: 'Update Brand',
					text: message,
					type: 'success',
				});
				this.isUpdateBrand = false;
				this.isShowEdit = false;
				this.fetchBrands();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Save Brand',
					text: message,
					type: 'error',
				});
				this.isUpdateBrand = false;
			};

			brandApi.updateBrand(this.selectedBrand._id.$oid, params, callback, errorCallback);
		},
		followUp(brand) {
			this.generateFollowUp(brand);
			this.isFollowUpVisible = true;
		},
		hideFollowUp() {
			this.followUpNumber = '';
			this.followUpName = '';
			this.followUpMessage = '';
			this.isFollowUpVisible = false;
		},
		generateFollowUp(brand) {
			const phone = brand.phone;
			const customerName = brand.name || '';
			const followUpMessages = '';
			this.followUpNumber = formatWaNumber(phone);
			this.followUpName = customerName;
			this.followUpMessage = followUpMessages;
		},
	},
	computed: {
		...mapGetters({
			filterDatePresets: 'filterDatePresets',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		params() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			return params;
		},
		isFilterOn() {
			return (
				this.since
				|| this.until
				|| this.keyword.length > 0
			);
		},
		isOtherSource() {
			return this.selectedSource === 'others';
		},
	},
	created() {
		this.initFilterDate();
		this.fetchBrands();
		this.fetchCategories();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.action-icon {
	font-size: 24px;
	cursor: pointer;
	margin-left: 0.5rem !important;
	margin-right: 0.5rem !important;
}

.mw-150 {
	min-width: 150px;
}

.mw-250 {
	min-width: 250px;
}
</style>
